import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from "@sentry/react";
import { useSearchParams } from 'react-router-dom';
import { getCommunityFeed } from '../../services/api';
import PostCard from '../../components/community/PostCard';
import TravelerCard from '../../components/community/TravelerCard';
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from "../../utils/analytics";

const Feed = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'recent';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);

  const observer = useRef();

  const fetchItems = async (tab, cursor, isReset = false) => {
    try {
      setLoading(true);
      const res = await getCommunityFeed(tab, cursor, 12);
      
      const newItems = res.data || [];
      const cursorNext = res.meta?.nextCursor;
      
      setItems(prev => isReset ? newItems : [...prev, ...newItems]);
      setNextCursor(cursorNext);
      setHasMore(!!cursorNext && newItems.length > 0);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      console.error("Failed to fetch feed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset state when tab changes
    setItems([]);
    setNextCursor(null);
    setHasMore(true);
    fetchItems(activeTab, null, true);
    
    // Update URL
    if (searchParams.get('tab') !== activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab]);

  // Memoized with the exact state it reads, so lastElementRef below always
  // rebinds to a fresh closure — the previous plain-function version was
  // recreated every render, but lastElementRef's own deps ([loading,
  // hasMore]) don't reliably change on every render (e.g. a tab switch
  // resets hasMore to true when it was already true, a no-op update), so the
  // IntersectionObserver could keep calling a loadMore closed over a stale
  // activeTab/nextCursor from before the switch.
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchItems(activeTab, nextCursor, false);
    }
  }, [activeTab, nextCursor, loading, hasMore]);

  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadMore]);

  const tabs = [
    { id: 'recent', label: 'Recent' },
    { id: 'trending', label: 'Trending' },
    { id: 'top-travelers', label: 'Top Travelers' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Community Feed</h1>
            <p className="text-gray-500">Discover stories and top contributors from the Romaya community.</p>
          </div>
          
          <div className="flex bg-white rounded-full p-1 border border-gray-200 shadow-sm self-start">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  trackEvent("community_feed_tab_selected", { tab: tab.id });
                  setActiveTab(tab.id);
                }}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeTab === tab.id 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((item, index) => {
            const isLast = items.length === index + 1;
            const ref = isLast ? lastElementRef : null;
            
            return (
              <div key={item.id || index} ref={ref}>
                {activeTab === 'top-travelers' ? (
                  <TravelerCard traveler={item} />
                ) : (
                  <PostCard post={item} />
                )}
              </div>
            );
          })}
        </div>

        {/* Loading / Empty States */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                {activeTab === 'top-travelers' ? (
                  <div className="h-24 bg-gray-200 rounded-[24px]"></div>
                ) : (
                  <div className="aspect-[4/4.6] bg-gray-200 rounded-[24px]"></div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-20">
            <h3 className="text-xl font-bold text-gray-700 mb-2">No {activeTab} posts found</h3>
            <p className="text-gray-500">Check back later for more content.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feed;
